import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, ClipboardCheck, ChefHat, Rocket, CheckCircle2, Send, Loader2, Sparkles } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { normalisePhoneGuinea, isValidPhoneGuinea } from "@/utils/phone";
import { DocUpload } from "@/components/DocUpload";
import { CUISINE_TYPES, SPECIALTIES, cuisineLabel } from "@/utils/cuisines";

const SparklesIcon = () => <Sparkles className="w-3 h-3" />;

const ZONES = [
  "Kaloum", "Dixinn", "Ratoma", "Matam", "Matoto",
  "Kipé", "Lambanyi", "Nongo", "Sonfonia", "Cosa", "Taouyah",
  "Hamdallaye", "Bambeto", "Enta",
];
const PHONE_RE = /^\+?\d[\d\s\-]{7,17}$/;

export default function BecomePartner() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    restaurant_name: "",
    manager_name: "",
    whatsapp: "",
    email: "",
    zone: "",
    address: "",
    cuisine_types: [],
    specialties: [],
    cuisine_other: "",
    featured_dishes: "",
    // Identité du responsable
    manager_phone: "",
    manager_id_doc_type: "cni",
    manager_id_front_url: "",
    manager_id_back_url: "",
    // Établissement
    rccm_number: "",
    storefront_photo_url: "",
    honor_declaration: false,
    // Hygiène (optionnel)
    hygiene_certificate_url: "",
    // Garant
    guarantor_name: "",
    guarantor_phone: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  // Téléphone du responsable = numéro WhatsApp par défaut (cas le plus courant)
  const [samePhone, setSamePhone] = useState(true);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCuisine = (v) => setForm((f) => ({
    ...f,
    cuisine_types: f.cuisine_types.includes(v)
      ? f.cuisine_types.filter((x) => x !== v)
      : f.cuisine_types.length >= 3 ? f.cuisine_types : [...f.cuisine_types, v],
  }));
  const toggleSpecialty = (v) => setForm((f) => ({
    ...f,
    specialties: f.specialties.includes(v) ? f.specialties.filter((x) => x !== v) : [...f.specialties, v],
  }));

  const validate = () => {
    const e = {};
    if (!form.restaurant_name.trim()) e.restaurant_name = t("becomePartner.required");
    if (!form.manager_name.trim()) e.manager_name = t("becomePartner.required");
    if (!form.whatsapp.trim()) e.whatsapp = t("becomePartner.required");
    else if (!isValidPhoneGuinea(form.whatsapp)) e.whatsapp = t("becomePartner.invalidPhone");
    if (!form.email.trim()) e.email = t("becomePartner.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = t("becomePartner.invalidEmail");
    if (!form.zone) e.zone = t("becomePartner.required");
    if (!form.address.trim()) e.address = t("becomePartner.required");
    // Identité du responsable
    if (!samePhone) {
      if (!form.manager_phone.trim()) e.manager_phone = t("becomePartner.required");
      else if (!isValidPhoneGuinea(form.manager_phone)) e.manager_phone = t("becomePartner.invalidPhone");
    }
    if (!form.manager_id_front_url) e.manager_id_front_url = "Photo recto de la pièce requise";
    if (form.manager_id_doc_type !== "passeport" && !form.manager_id_back_url) e.manager_id_back_url = "Photo verso de la pièce requise";
    // Établissement
    if (!form.storefront_photo_url) e.storefront_photo_url = "Photo de la devanture requise";
    if (!form.honor_declaration) e.honor_declaration = "Vous devez certifier sur l'honneur l'existence de l'établissement";
    // Garant
    if (!form.guarantor_name.trim()) e.guarantor_name = t("becomePartner.required");
    if (!form.guarantor_phone.trim()) e.guarantor_phone = t("becomePartner.required");
    else if (!isValidPhoneGuinea(form.guarantor_phone)) e.guarantor_phone = t("becomePartner.invalidPhone");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post("/candidatures/restaurants", {
        restaurant_name: form.restaurant_name.trim(),
        manager_name: form.manager_name.trim(),
        whatsapp: "+" + normalisePhoneGuinea(form.whatsapp),
        email: form.email.trim().toLowerCase(),
        zone: form.zone,
        address: form.address.trim(),
        cuisine_types: form.cuisine_types,
        specialties: form.specialties,
        cuisine_other: "",
        featured_dishes: form.featured_dishes.trim(),
        manager_phone: samePhone
          ? "+" + normalisePhoneGuinea(form.whatsapp)
          : "+" + normalisePhoneGuinea(form.manager_phone),
        manager_id_doc_type: form.manager_id_doc_type,
        manager_id_front_url: form.manager_id_front_url,
        manager_id_back_url: form.manager_id_back_url || null,
        rccm_number: form.rccm_number.trim() || null,
        has_rccm: Boolean(form.rccm_number.trim()),
        storefront_photo_url: form.storefront_photo_url,
        honor_declaration: form.honor_declaration,
        hygiene_certificate_url: form.hygiene_certificate_url || null,
        guarantor_name: form.guarantor_name.trim(),
        guarantor_phone: "+" + normalisePhoneGuinea(form.guarantor_phone),
      });
      setConfirmation({
        manager: data.manager_name || form.manager_name.trim(),
        restaurant: data.restaurant_name || form.restaurant_name.trim(),
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setForm({
      restaurant_name: "", manager_name: "", whatsapp: "", email: "", zone: "", address: "",
      cuisine_types: [], specialties: [], cuisine_other: "", featured_dishes: "",
      manager_phone: "", manager_id_doc_type: "cni", manager_id_front_url: "", manager_id_back_url: "",
      rccm_number: "", storefront_photo_url: "", honor_declaration: false,
      hygiene_certificate_url: "", guarantor_name: "", guarantor_phone: "",
    });
    setSamePhone(true);
    setErrors({});
    setConfirmation(null);
  };

  if (confirmation) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-[#E7F6EE] text-brand-green grid place-items-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-brand-green mb-2">{t("becomePartner.kicker")}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-3">
          {t("becomePartner.successTitle", { manager: confirmation.manager })}
        </h1>
        <p className="text-mute text-lg mb-6">
          {t("becomePartner.successMsg", { restaurant: confirmation.restaurant })}
        </p>
        <button
          onClick={reset}
          data-testid="partner-app-reset"
          className="rounded-full bg-white border border-sand hover:border-brand-red hover:text-brand-red px-5 py-3 font-semibold"
        >
          {t("becomePartner.anotherOne")}
        </button>
      </div>
    );
  }

  const steps = [
    { icon: <FileText className="w-5 h-5" />, title: t("becomePartner.step1Title"), desc: t("becomePartner.step1Desc") },
    { icon: <ClipboardCheck className="w-5 h-5" />, title: t("becomePartner.step2Title"), desc: t("becomePartner.step2Desc") },
    { icon: <ChefHat className="w-5 h-5" />, title: t("becomePartner.step3Title"), desc: t("becomePartner.step3Desc") },
    { icon: <Rocket className="w-5 h-5" />, title: t("becomePartner.step4Title"), desc: t("becomePartner.step4Desc") },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-2">{t("becomePartner.kicker")}</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1]">
        {t("becomePartner.title")}
      </h1>
      <p className="mt-4 text-lg text-mute max-w-2xl">{t("becomePartner.subtitle")}</p>

      <section className="mt-10">
        <h2 className="font-display text-xl sm:text-2xl font-semibold mb-4">{t("becomePartner.stepsTitle")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {steps.map((s, i) => (
            <div key={i} data-testid={`partner-step-${i + 1}`} className="bg-white border border-sand rounded-2xl p-5 flex gap-4 items-start">
              <div className="relative shrink-0">
                <div className="w-10 h-10 rounded-full bg-[#FFF5F6] text-brand-red grid place-items-center">{s.icon}</div>
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-ink text-white text-[10px] font-semibold grid place-items-center">{i + 1}</span>
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold mb-1">{s.title}</h3>
                <p className="text-sm text-mute leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AI DISH PHOTO — differentiator marketing block */}
      <section
        className="mt-10 rounded-3xl overflow-hidden border border-brand-yellow/40 bg-gradient-to-br from-[#FFF7E6] via-white to-[#FFF5F6]"
        data-testid="partner-ai-highlight"
      >
        <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] items-stretch">
          <div className="p-6 sm:p-8 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 self-start rounded-full bg-brand-red text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 mb-3">
              <SparklesIcon /> Exclusivité NelDaari
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
              {t("bp.noPhotog")} <span className="text-brand-red">{t("bp.noPhotogAccent")}</span>
            </h2>
            <p className="mt-3 text-mute leading-relaxed">
              {t("bp.aiText1")} {t("bp.aiText2")} <b>{t("bp.aiText3")}</b>. {t("bp.aiText4")}
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex gap-2"><span className="text-brand-green">✓</span> Photo cadrée carrée, prête pour les réseaux sociaux.</li>
              <li className="flex gap-2"><span className="text-brand-green">✓</span> Ambiance chaude, style guinéen éditorial.</li>
              <li className="flex gap-2"><span className="text-brand-green">✓</span> Gratuit et illimité pour vos plats du menu.</li>
              <li className="flex gap-2"><span className="text-brand-green">✓</span> Générée en quelques secondes, disponible à tout moment.</li>
              <li className="flex gap-2"><span className="text-brand-green">✓</span> Aucune compétence technique ou photo requise.</li>
            </ul>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="#partner-form" data-testid="partner-ai-cta" className="rounded-full bg-brand-red hover:bg-[#C81F2D] text-white px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                Je m&apos;inscris — c&apos;est parti
              </a>
              <span className="text-xs text-mute self-center">{t("bp.noCommitment")}</span>
            </div>
          </div>
          <div className="relative min-h-[220px] bg-[#1A1A1A]">
            <img
              src="https://neldaari.blob.core.windows.net/neldaari-image/uploads/restaurant/user_9f7ced496d75/202607/154a3c44e3503ab8.jpg"
              alt="Exemple de photo IA générée"
              className="absolute inset-0 w-full h-full object-cover opacity-95"
              loading="lazy"
            />
            <div className="absolute bottom-3 left-3 rounded-full bg-black/60 backdrop-blur px-3 py-1 text-white text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5">
              <SparklesIcon /> Photo générée en 7 s
            </div>
          </div>
        </div>
      </section>

      <section id="partner-form" className="mt-10 bg-elevated rounded-3xl p-6 sm:p-8">
        <h2 className="font-display text-xl sm:text-2xl font-semibold mb-1">{t("becomePartner.formTitle")}</h2>
        <p className="text-sm text-mute mb-5">{t("becomePartner.formIntro")}</p>

        <form onSubmit={submit} data-testid="partner-app-form" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("becomePartner.restaurantName")} error={errors.restaurant_name}>
            <input
              value={form.restaurant_name}
              onChange={(e) => set("restaurant_name", e.target.value)}
              placeholder={t("becomePartner.restaurantNamePh")}
              data-testid="partner-app-restaurant"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
            />
          </Field>
          <Field label={t("becomePartner.managerName")} error={errors.manager_name}>
            <input
              value={form.manager_name}
              onChange={(e) => set("manager_name", e.target.value)}
              placeholder={t("becomePartner.managerNamePh")}
              data-testid="partner-app-manager"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
            />
          </Field>
          <Field label={t("becomePartner.whatsapp")} error={errors.whatsapp}>
            <input
              value={form.whatsapp}
              onChange={(e) => set("whatsapp", e.target.value)}
              placeholder={t("becomePartner.whatsappPh")}
              inputMode="tel"
              data-testid="partner-app-whatsapp"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
            />
          </Field>
          <Field label={t("becomePartner.email")} error={errors.email}>
            <input
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder={t("becomePartner.emailPh")}
              inputMode="email"
              type="email"
              data-testid="partner-app-email"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
            />
          </Field>
          <Field label={t("becomePartner.zone")} error={errors.zone}>
            <select
              value={form.zone}
              onChange={(e) => set("zone", e.target.value)}
              data-testid="partner-app-zone"
              className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
            >
              <option value="">{t("becomePartner.zonePh")}</option>
              {ZONES.map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label={t("becomePartner.address")} error={errors.address}>
              <input
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder={t("becomePartner.addressPh")}
                data-testid="partner-app-address"
                className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("becomePartner.cuisines")}</span>
            <span className="text-[11px] text-mute block mb-2">{t("becomePartner.cuisineMax3")}</span>
            <div className="flex flex-wrap gap-2">
              {CUISINE_TYPES.map((c) => {
                const active = form.cuisine_types.includes(c.v);
                const off = !active && form.cuisine_types.length >= 3;
                return (
                  <button
                    key={c.v}
                    type="button"
                    disabled={off}
                    aria-pressed={active}
                    onClick={() => toggleCuisine(c.v)}
                    data-testid={`partner-app-cuisine-${c.v}`}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                      active ? "bg-ink text-white border-ink" : off ? "bg-white border-sand opacity-40 cursor-not-allowed" : "bg-white border-sand hover:border-brand-red"
                    }`}
                  >
                    {cuisineLabel(c.v, i18n.language)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-2">
            <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("becomePartner.specialties")}</span>
            <span className="text-[11px] text-mute block mb-2">{t("becomePartner.specialtiesHint")}</span>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map((s) => {
                const active = form.specialties.includes(s.v);
                return (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => toggleSpecialty(s.v)}
                    data-testid={`partner-app-specialty-${s.v}`}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                      active ? "bg-brand-red text-white border-brand-red" : "bg-white border-sand hover:border-brand-red"
                    }`}
                  >
                    {cuisineLabel(s.v, i18n.language)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-2">
            <Field label={t("becomePartner.featuredDishes")}>
              <textarea
                value={form.featured_dishes}
                onChange={(e) => set("featured_dishes", e.target.value)}
                placeholder={t("becomePartner.featuredDishesPh")}
                rows={3}
                data-testid="partner-app-featured"
                className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red resize-none"
              />
            </Field>
          </div>
          {/* ── Identité du responsable ── */}
          <div className="md:col-span-2 mt-2 pt-4 border-t border-sand">
            <h3 className="font-display text-lg font-semibold">{t("bp.idTitle")}</h3>
            <p className="text-xs text-mute mt-0.5 mb-3">{t("bp.idHint")}</p>
            <div className="grid grid-cols-2 gap-2 max-w-md mb-4">
              {[
                { v: "cni", label: "CNI guinéenne" },
                { v: "passeport", label: "Passeport" },
              ].map((o) => (
                <button key={o.v} type="button" onClick={() => set("manager_id_doc_type", o.v)}
                  data-testid={`partner-app-idtype-${o.v}`}
                  className={`rounded-xl px-3 py-3 text-sm font-semibold border transition-colors ${
                    form.manager_id_doc_type === o.v ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DocUpload label={`${form.manager_id_doc_type === "passeport" ? "Passeport" : "CNI"} — recto`} value={form.manager_id_front_url}
                onUploaded={(url) => set("manager_id_front_url", url)} error={errors.manager_id_front_url} testid="partner-app-id-front" />
              {form.manager_id_doc_type !== "passeport" && (
                <DocUpload label="CNI — verso" value={form.manager_id_back_url}
                  onUploaded={(url) => set("manager_id_back_url", url)} error={errors.manager_id_back_url} testid="partner-app-id-back" />
              )}
            </div>
            <div className="mt-4 max-w-md space-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm font-medium select-none">
                <input
                  type="checkbox"
                  checked={samePhone}
                  onChange={(e) => {
                    setSamePhone(e.target.checked);
                    if (e.target.checked) {
                      set("manager_phone", "");
                      setErrors((prev) => ({ ...prev, manager_phone: undefined }));
                    }
                  }}
                  data-testid="partner-app-same-phone"
                  className="w-4 h-4 accent-brand-red"
                />
                C&apos;est le même numéro que le WhatsApp
              </label>
              {!samePhone && (
                <Field label="Téléphone du responsable" error={errors.manager_phone}>
                  <input value={form.manager_phone} onChange={(e) => set("manager_phone", e.target.value)}
                    placeholder="+224 6XX XX XX XX" inputMode="tel" data-testid="partner-app-manager-phone"
                    className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red" />
                </Field>
              )}
            </div>
          </div>

          {/* ── Existence de l'établissement ── */}
          <div className="md:col-span-2 mt-2 pt-4 border-t border-sand">
            <h3 className="font-display text-lg font-semibold">{t("bp.existTitle")}</h3>
            <p className="text-xs text-mute mt-0.5 mb-3">{t("bp.existHint")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Numéro RCCM (optionnel)">
                <input value={form.rccm_number} onChange={(e) => set("rccm_number", e.target.value)}
                  placeholder="Ex : RCCM/GC-KAL/012345" data-testid="partner-app-rccm-number"
                  className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red" />
              </Field>
              <DocUpload label="Photo de la devanture / façade (obligatoire)" value={form.storefront_photo_url}
                onUploaded={(url) => set("storefront_photo_url", url)} error={errors.storefront_photo_url} testid="partner-app-storefront" />
            </div>
          </div>

          {/* ── Hygiène (optionnel) ── */}
          <div className="md:col-span-2 mt-2 pt-4 border-t border-sand">
            <h3 className="font-display text-lg font-semibold">{t("bp.hygieneTitle")} <span className="text-sm font-normal text-mute">({t("bp.optional")})</span></h3>
            <p className="text-xs text-mute mt-0.5 mb-3">{t("bp.hygieneHint")}</p>
            <div className="max-w-md">
              <DocUpload label="Certificat d'hygiène" value={form.hygiene_certificate_url}
                onUploaded={(url) => set("hygiene_certificate_url", url)} testid="partner-app-hygiene" />
            </div>
          </div>

          {/* ── Garant ── */}
          <div className="md:col-span-2 mt-2 pt-4 border-t border-sand">
            <h3 className="font-display text-lg font-semibold">{t("bp.guarantorTitle")}</h3>
            <p className="text-xs text-mute mt-0.5 mb-3">{t("bp.guarantorHint")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nom complet du garant" error={errors.guarantor_name}>
                <input value={form.guarantor_name} onChange={(e) => set("guarantor_name", e.target.value)}
                  placeholder="Ex : Mamadou Diallo" data-testid="partner-app-guarantor-name"
                  className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red" />
              </Field>
              <Field label="Téléphone du garant" error={errors.guarantor_phone}>
                <input value={form.guarantor_phone} onChange={(e) => set("guarantor_phone", e.target.value)}
                  placeholder="+224 6XX XX XX XX" inputMode="tel" data-testid="partner-app-guarantor-phone"
                  className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red" />
              </Field>
            </div>
          </div>

          {/* ── Déclaration sur l'honneur ── */}
          <div className="md:col-span-2 mt-2 pt-4 border-t border-sand">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="partner-app-honor-label">
              <input type="checkbox" checked={form.honor_declaration}
                onChange={(e) => set("honor_declaration", e.target.checked)}
                data-testid="partner-app-honor"
                className="mt-1 w-4 h-4 accent-[#E32636]" />
              <span className="text-sm leading-relaxed">
                Je certifie que cet établissement <b>existe réellement</b> et que les informations fournies sont <b>exactes</b>.
              </span>
            </label>
            {errors.honor_declaration && <span className="text-xs text-brand-red mt-1 block">{errors.honor_declaration}</span>}
          </div>

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="partner-app-submit"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-6 py-3 font-semibold"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {busy ? t("becomePartner.submitting") : t("becomePartner.submit")}
            </button>
          </div>
        </form>
        <p className="mt-6 text-sm text-mute text-center" data-testid="become-partner-login-hint">
          {t("bp.alreadyPartner")} <Link to="/espace-partenaires" data-testid="become-partner-login-link" className="text-brand-red font-semibold">{t("becomeDriver.partnerLoginLink")}</Link>
        </p>
      </section>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-mute block mb-2">{label}</span>
      {children}
      {error && <span className="text-xs text-brand-red mt-1 block">{error}</span>}
    </label>
  );
}
